/**
 * Emergency Status Service
 * Provides real-time monitoring of emergency shutdown status across the application
 * 
 * This service addresses the critical security vulnerability where emergency shutdown
 * did not immediately block AI access for logged-in users.
 */

import { apiService } from './api';
import type { AISystemStatus } from '../types/aiPermission';

// Emergency status event types
export interface EmergencyStatusEvent {
  type: 'status_change' | 'websocket_disconnection' | 'access_blocked';
  status: 'active' | 'emergency_shutdown' | 'disabled';
  timestamp: string;
  reason?: string;
  level?: 'emergency' | 'global' | 'provider_api';
  message?: string;
}

export interface EmergencyStatusState {
  isEmergencyActive: boolean;
  globalAIEnabled: boolean;
  providerAPIEnabled: boolean;
  lastChecked: Date;
  blockingLevel: 'none' | 'emergency' | 'global' | 'provider_api';
  blockingReason?: string;
}

// Event listener type
type EmergencyStatusListener = (event: EmergencyStatusEvent) => void;

/**
 * Emergency Status Service
 * Singleton service for monitoring emergency shutdown status
 */
class EmergencyStatusService {
  private status: EmergencyStatusState = {
    isEmergencyActive: false,
    globalAIEnabled: true,
    providerAPIEnabled: true,
    lastChecked: new Date(),
    blockingLevel: 'none'
  };

  private listeners: EmergencyStatusListener[] = [];
  private pollingInterval: NodeJS.Timeout | null = null;
  private isPolling = false;
  private readonly POLL_INTERVAL = 3000; // Poll every 3 seconds for real-time updates

  /**
   * Initialize emergency status monitoring
   */
  public async initialize(): Promise<void> {
    if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
      console.log('[EmergencyStatusService] Initializing emergency status monitoring');
    }
    
    try {
      // Initial status check
      await this.checkStatus();
      
      // Start polling for status changes
      this.startPolling();
      
      // Listen for browser visibility changes to resume polling
      document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
      
      if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
        console.log('[EmergencyStatusService] Emergency status monitoring initialized');
      }
    } catch (error) {
      if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
        console.error('[EmergencyStatusService] Failed to initialize:', error);
      }
      
      // Check if it's a network/auth error vs actual emergency
      const isNetworkError = error instanceof Error && (
        error.message.includes('Authentication') ||
        error.message.includes('Network') ||
        error.message.includes('fetch') ||
        error.message.includes('401') ||
        error.message.includes('403') ||
        error.message.includes('404')
      );
      
      if (isNetworkError) {
        // For network/auth errors, allow AI access and retry later
        if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
          console.log('[EmergencyStatusService] Network/auth error detected, allowing AI access and retrying...');
        }
        this.status.blockingLevel = 'none';
        this.status.blockingReason = 'Unable to verify status - allowing access';
        
        // Retry initialization after a delay
        setTimeout(() => {
          if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
            console.log('[EmergencyStatusService] Retrying initialization...');
          }
          this.initialize().catch(e => {
            if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
              console.warn('[EmergencyStatusService] Retry failed:', e);
            }
          });
        }, 5000);
      } else {
        // For other errors, fail secure
        if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
          console.log('[EmergencyStatusService] Unknown error, failing secure');
        }
        this.status.blockingLevel = 'emergency';
      }
      
      this.notifyListeners({
        type: 'status_change',
        status: isNetworkError ? 'active' : 'emergency_shutdown',
        timestamp: new Date().toISOString(),
        reason: isNetworkError ? 'Status check failed - allowing access' : 'Initialization failed - failing secure',
        message: 'Unable to verify AI system status'
      });
    }
  }

  /**
   * Start polling for emergency status changes
   */
  private startPolling(): void {
    if (this.isPolling) return;
    
    this.isPolling = true;
    if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
      console.log('[EmergencyStatusService] Starting emergency status polling');
    }
    
    this.pollingInterval = setInterval(async () => {
      try {
        await this.checkStatus();
      } catch (error) {
        if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
          console.error('[EmergencyStatusService] Polling error:', error);
        }
        
        // Check if it's a network/auth error vs actual emergency
        const isNetworkError = error instanceof Error && (
          error.message.includes('Authentication') ||
          error.message.includes('Network') ||
          error.message.includes('fetch') ||
          error.message.includes('401') ||
          error.message.includes('403') ||
          error.message.includes('404')
        );
        
        if (isNetworkError) {
          // For network errors during polling, don't change status - keep last known good status
          if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
            console.log('[EmergencyStatusService] Network error during polling, maintaining current status');
          }
          this.status.lastChecked = new Date();
          this.status.blockingReason = 'Status check failed - using last known status';
        } else {
          // For unknown errors, fail secure
          if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
            console.log('[EmergencyStatusService] Unknown polling error, failing secure');
          }
          this.updateStatus({
            isEmergencyActive: true,
            globalAIEnabled: false,
            providerAPIEnabled: false,
            lastChecked: new Date(),
            blockingLevel: 'emergency',
            blockingReason: 'Status check failed'
          });
        }
      }
    }, this.POLL_INTERVAL);
  }

  /**
   * Stop polling for emergency status
   */
  private stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    this.isPolling = false;
    if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
      console.log('[EmergencyStatusService] Stopped emergency status polling');
    }
  }

  /**
   * Handle browser visibility changes to manage polling
   */
  private handleVisibilityChange(): void {
    if (document.hidden) {
      if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
        console.log('[EmergencyStatusService] Browser hidden, reducing polling frequency');
      }
      // Continue polling but less frequently when hidden
    } else {
      if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
        console.log('[EmergencyStatusService] Browser visible, resuming normal polling');
      }
      // Immediately check status when browser becomes visible
      this.checkStatus();
    }
  }

  /**
   * Check current emergency status from the backend
   */
  private async checkStatus(): Promise<void> {
    try {
      if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
        console.log('[EmergencyStatusService] Checking emergency status...');
      }
      
      // _skipAuthFailure: this is a background status poll. Its catch below is
      // designed to keep the last-known status on auth/network errors, so a
      // transient 401 must NOT force a global logout (which would fire inside
      // the interceptor before that catch runs).
      const systemStatus: AISystemStatus = await apiService.get('/system/ai-status/', {
        _skipAuthFailure: true,
      });
      
      const previousStatus = { ...this.status };
      const newStatus: EmergencyStatusState = {
        isEmergencyActive: systemStatus.emergency_shutdown_active || false,
        globalAIEnabled: systemStatus.global_enabled || false,
        providerAPIEnabled: systemStatus.ai_provider_enabled !== false, // Default to true if undefined
        lastChecked: new Date(),
        blockingLevel: this.determineBlockingLevel(systemStatus),
        blockingReason: this.determineBlockingReason(systemStatus)
      };

      if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
        console.log('[EmergencyStatusService] Status check result:', {
          isEmergencyActive: newStatus.isEmergencyActive,
          globalAIEnabled: newStatus.globalAIEnabled,
          providerAPIEnabled: newStatus.providerAPIEnabled,
          blockingLevel: newStatus.blockingLevel
        });
      }

      // Check if status changed
      if (this.hasStatusChanged(previousStatus, newStatus)) {
        this.updateStatus(newStatus);
        
        // Notify listeners of status change
        this.notifyListeners({
          type: 'status_change',
          status: newStatus.isEmergencyActive ? 'emergency_shutdown' : 
                  newStatus.blockingLevel !== 'none' ? 'disabled' : 'active',
          timestamp: new Date().toISOString(),
          reason: newStatus.blockingReason,
          level: newStatus.blockingLevel === 'none' ? undefined : newStatus.blockingLevel as any,
          message: this.getStatusMessage(newStatus)
        });
      }
    } catch (error) {
      if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
        console.error('[EmergencyStatusService] Status check failed:', error);
      }
      throw error; // Re-throw to be handled by caller
    }
  }

  /**
   * Determine the blocking level based on system status
   */
  private determineBlockingLevel(systemStatus: AISystemStatus): EmergencyStatusState['blockingLevel'] {
    if (systemStatus.emergency_shutdown_active) {
      return 'emergency';
    }
    
    if (!systemStatus.global_enabled) {
      return 'global';
    }
    
    if (systemStatus.ai_provider_enabled === false) {
      return 'provider_api';
    }
    
    return 'none';
  }

  /**
   * Determine the blocking reason based on system status
   */
  private determineBlockingReason(systemStatus: AISystemStatus): string | undefined {
    if (systemStatus.emergency_shutdown_active) {
      return 'Emergency shutdown is active';
    }
    
    if (!systemStatus.global_enabled) {
      return 'AI services globally disabled';
    }
    
    if (systemStatus.ai_provider_enabled === false) {
      return 'AI provider API disabled';
    }
    
    return undefined;
  }

  /**
   * Check if emergency status has changed
   */
  private hasStatusChanged(previous: EmergencyStatusState, current: EmergencyStatusState): boolean {
    return (
      previous.isEmergencyActive !== current.isEmergencyActive ||
      previous.globalAIEnabled !== current.globalAIEnabled ||
      previous.providerAPIEnabled !== current.providerAPIEnabled ||
      previous.blockingLevel !== current.blockingLevel
    );
  }

  /**
   * Update the internal status and log changes
   */
  private updateStatus(newStatus: EmergencyStatusState): void {
    const previousLevel = this.status.blockingLevel;
    this.status = newStatus;
    
    if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
      console.log('[EmergencyStatusService] Status updated:', {
        from: previousLevel,
        to: newStatus.blockingLevel,
        isEmergencyActive: newStatus.isEmergencyActive,
        reason: newStatus.blockingReason
      });
    }
  }

  /**
   * Get human-readable status message
   */
  private getStatusMessage(status: EmergencyStatusState): string {
    if (status.isEmergencyActive) {
      return 'AI systems are currently shut down due to emergency maintenance';
    }
    
    if (!status.globalAIEnabled) {
      return 'AI services are globally disabled';
    }
    
    if (!status.providerAPIEnabled) {
      return 'AI provider services are temporarily unavailable';
    }
    
    return 'AI services are operational';
  }

  /**
   * Notify all listeners of status change
   */
  private notifyListeners(event: EmergencyStatusEvent): void {
    if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
      console.log('[EmergencyStatusService] Notifying listeners of event:', event.type);
    }
    
    this.listeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
          console.error('[EmergencyStatusService] Error notifying listener:', error);
        }
      }
    });
  }

  // Public API methods

  /**
   * Get current emergency status
   */
  public getStatus(): EmergencyStatusState {
    return { ...this.status };
  }

  /**
   * Check if AI interactions should be blocked
   */
  public isAIBlocked(): boolean {
    return this.status.blockingLevel !== 'none';
  }

  /**
   * Check if emergency shutdown is active
   */
  public isEmergencyShutdownActive(): boolean {
    return this.status.isEmergencyActive;
  }

  /**
   * Get blocking reason for user display
   */
  public getBlockingReason(): string | null {
    return this.status.blockingReason || null;
  }

  /**
   * Add listener for status changes
   */
  public addListener(listener: EmergencyStatusListener): () => void {
    if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
      console.log('[EmergencyStatusService] Adding status change listener');
    }
    this.listeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
        if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
          console.log('[EmergencyStatusService] Removed status change listener');
        }
      }
    };
  }

  /**
   * Force immediate status check (for critical operations)
   */
  public async forceStatusCheck(): Promise<EmergencyStatusState> {
    if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
      console.log('[EmergencyStatusService] Force checking emergency status');
    }
    await this.checkStatus();
    return this.getStatus();
  }

  /**
   * Handle WebSocket disconnection due to emergency shutdown
   */
  public handleEmergencyDisconnection(reason?: string): void {
    if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
      console.log('[EmergencyStatusService] Handling emergency WebSocket disconnection:', reason);
    }
    
    // Update status to reflect emergency shutdown
    this.updateStatus({
      ...this.status,
      isEmergencyActive: true,
      blockingLevel: 'emergency',
      blockingReason: reason || 'WebSocket disconnected due to emergency shutdown'
    });

    // Notify listeners
    this.notifyListeners({
      type: 'websocket_disconnection',
      status: 'emergency_shutdown',
      timestamp: new Date().toISOString(),
      reason: reason || 'Emergency disconnection',
      level: 'emergency',
      message: 'WebSocket connection terminated due to emergency shutdown'
    });
  }

  /**
   * Report blocked access attempt
   */
  public reportBlockedAccess(action: string, reason?: string): void {
    if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
      console.log('[EmergencyStatusService] Reporting blocked access attempt:', action, reason);
    }
    
    this.notifyListeners({
      type: 'access_blocked',
      status: this.status.isEmergencyActive ? 'emergency_shutdown' : 'disabled',
      timestamp: new Date().toISOString(),
      reason: reason || this.status.blockingReason || 'Access blocked',
      level: this.status.blockingLevel === 'none' ? 'global' : this.status.blockingLevel as any,
      message: `Access to ${action} was blocked: ${reason || 'Service unavailable'}`
    });
  }

  /**
   * Clean up resources when service is no longer needed
   */
  public cleanup(): void {
    if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
      console.log('[EmergencyStatusService] Cleaning up emergency status service');
    }
    
    this.stopPolling();
    this.listeners.splice(0, this.listeners.length);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    
    if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
      console.log('[EmergencyStatusService] Emergency status service cleaned up');
    }
  }
}

// Create singleton instance
export const emergencyStatusService = new EmergencyStatusService();

// Export service class for testing
export { EmergencyStatusService };